import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { DataSource, InventoryRow } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { ChartCard, FigureTable } from "@/components/charts/ChartCard";
import { CategoryBarChart, RankedBarChart } from "@/components/charts/lazy";
import { Card, CardContent } from "@/components/ui/card";
import { formatCount } from "@/lib/chart-format";
import { fetchAllPages } from "@/modules/reports/lib/pagination";
import { aggregateInventoryByWarehouse } from "@/modules/reports/inventory";
import { ReportHeader } from "@/modules/reports/ReportHeader";

interface InventoryReportData {
  rows: InventoryRow[];
  lowStockRows: InventoryRow[];
}

async function loadInventory(data: DataSource): Promise<InventoryReportData> {
  const [rows, lowStockRows] = await Promise.all([
    fetchAllPages((query) => data.inventory.list(query)),
    fetchAllPages((query) => data.inventory.listLowStock(query)),
  ]);
  return { rows, lowStockRows };
}

function Total({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <p className="text-muted-foreground text-sm">{label}</p>
      <p className="text-2xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function warehouseName(row: { warehouseName: string; warehouseCode: string }) {
  return (
    <>
      {row.warehouseName}{" "}
      <span className="text-muted-foreground">({row.warehouseCode})</span>
    </>
  );
}

/**
 * Stock on hand, low stock and in-transit quantity, by warehouse
 * (FR-DSH-4, ticket #96).
 *
 * `stockOnHand` and `inTransit` sum `InventoryRow.available` /
 * `.inTransit`, both plain integers on the row already — unlike a revenue
 * figure there is no decimal-string summing hazard here, so a normal
 * `reduce` is exact. `lowStockCount` intentionally does not repeat FR-INV-3's
 * threshold comparison; it counts rows `InventoryRepository.listLowStock`
 * already decided qualify, so a variant with no threshold of its own falls
 * back to the same default here as it does on the Low Stock screen.
 */
export function Inventory() {
  const data = useDataSource();

  const query = useQuery({
    queryKey: ["reports", "inventory"],
    queryFn: () => loadInventory(data),
  });

  return (
    <div className="flex flex-col gap-6">
      <ReportHeader readAt={query.data ? query.dataUpdatedAt : undefined} />
      <InventoryBody query={query} />
    </div>
  );
}

function InventoryBody({
  query,
}: {
  query: UseQueryResult<InventoryReportData>;
}) {
  if (query.isError) {
    return (
      <LoadFailed
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  if (query.isPending) {
    return <TableSkeleton />;
  }

  const byWarehouse = aggregateInventoryByWarehouse(
    query.data.rows,
    query.data.lowStockRows,
  );
  const totalStockOnHand = query.data.rows.reduce(
    (sum, row) => sum + row.available,
    0,
  );
  const totalInTransit = query.data.rows.reduce(
    (sum, row) => sum + row.inTransit,
    0,
  );

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardContent className="flex flex-wrap gap-8">
          <Total label="Stock on hand" value={totalStockOnHand} />
          <Total
            label="Low stock variants"
            value={query.data.lowStockRows.length}
          />
          <Total label="In transit" value={totalInTransit} />
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard
          title="Units by warehouse"
          description="Units on hand and units in transit at each warehouse, side by side. In transit exists only in the ERP — Shopify has no concept of stock between two of its own locations."
          table={
            <FigureTable
              rows={byWarehouse}
              rowKey={(row) => row.warehouseId}
              columns={[
                { header: "Warehouse", cell: warehouseName },
                {
                  header: "Stock on hand",
                  numeric: true,
                  cell: (row) => row.stockOnHand,
                },
                {
                  header: "In transit",
                  numeric: true,
                  cell: (row) => row.inTransit,
                },
              ]}
            />
          }
        >
          <CategoryBarChart
            categories={byWarehouse.map((row) => ({
              key: row.warehouseId,
              label: row.warehouseName,
              values: {
                stockOnHand: row.stockOnHand,
                inTransit: row.inTransit,
              },
            }))}
            series={[
              {
                key: "stockOnHand",
                label: "Stock on hand",
                color: "var(--primary)",
              },
              {
                key: "inTransit",
                label: "In transit",
                color: "var(--series-1)",
              },
            ]}
            formatValue={formatCount}
            ariaLabel={`Units by warehouse: ${byWarehouse
              .map(
                (row) =>
                  `${row.warehouseName} ${String(row.stockOnHand)} on hand, ${String(row.inTransit)} in transit`,
              )
              .join("; ")}.`}
          />
        </ChartCard>

        {/* Its own chart, not a third bar beside the units: this counts
            variants, and one axis cannot be both. */}
        <ChartCard
          title="Low stock variants by warehouse"
          description="How many variants are at or below their low-stock threshold at each warehouse — the same rows the Low Stock screen lists."
          table={
            <FigureTable
              rows={byWarehouse}
              rowKey={(row) => row.warehouseId}
              columns={[
                { header: "Warehouse", cell: warehouseName },
                {
                  header: "Low stock variants",
                  numeric: true,
                  cell: (row) => row.lowStockCount,
                },
              ]}
            />
          }
        >
          <RankedBarChart
            rows={[...byWarehouse]
              .sort((a, b) => b.lowStockCount - a.lowStockCount)
              .map((row) => ({
                key: row.warehouseId,
                label: row.warehouseName,
                value: row.lowStockCount,
              }))}
            valueLabel="Low stock variants"
            formatValue={formatCount}
            color="var(--series-8)"
            ariaLabel={`Low stock variants by warehouse: ${byWarehouse
              .map((row) => `${row.warehouseName} ${String(row.lowStockCount)}`)
              .join(", ")}.`}
          />
        </ChartCard>
      </div>
    </div>
  );
}
